import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Ellipsis, FileCode2, FilePlus, FileType, Folder, FolderOpen, FolderPlus, Kanban, LayoutDashboard, Library, Mail, MailPlus, PanelLeftClose, Paperclip, Pin, Plus, ScrollText, SlidersHorizontal, Table2, Trash } from "lucide-react";
import { toast } from "sonner";
import { NOTE_KIND, WORKSPACE_SECTIONS, type WorkspaceSection } from "@brainlog/shared";
import { cn } from "@/lib/utils";
import { del, errorMessage, get, patch, post } from "@/lib/api";
import { allTabs, openNote, openView, useTabs, useUi, type ViewName } from "@/lib/store";
import { qk, usePrefs, useTree } from "@/lib/queries";
import type { FolderRow, NoteRow, Workspace } from "@/lib/types";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { KindIcon } from "@/features/notes/kinds";
import { NewManuscriptDialog } from "@/features/manuscripts/NewManuscriptDialog";
import { MoveDialog } from "@/features/notes/MoveDialog";
import { PromptDialog } from "@/components/PromptDialog";
import { NoteTitle } from "@/features/crypto/UnlockPanel";
import { createNoteIn } from "@/features/crypto/createNote";
import { forgetPlaintextCopies } from "@/features/notes/collab";
import { ENCRYPTED_TITLE_PLACEHOLDER } from "@brainlog/shared";
import { unlockWithPersonalKey, usePersonalUnlocked } from "@/features/crypto/keyring";
import { useWorkspaceEncryption } from "@/features/crypto/ProjectGate";
import { ckey, type CorrespondenceList } from "@/features/correspondence/types";
import { setArchived } from "@/features/archive/ArchiveView";

const RECORD_FILTERS: { key: string; label: string; kinds: string[] }[] = [
  { key: "all", label: "All records", kinds: NOTE_KIND.values.filter((k) => k !== "note") },
  { key: "log", label: "Research log", kinds: ["research_log"] },
  { key: "meetings", label: "Meetings", kinds: ["meeting"] },
  { key: "decisions", label: "Decisions", kinds: ["decision"] },
  { key: "questions", label: "Questions & hypotheses", kinds: ["research_question", "hypothesis"] },
  { key: "studies", label: "Studies", kinds: ["study_plan", "study_execution"] },
  { key: "results", label: "Results", kinds: ["result"] },
  { key: "reading", label: "Reading notes", kinds: ["reading_note", "search_log"] },
];

function useExpanded(wsId: string) {
  const key = `brainlog-claude-expanded:${wsId}`;
  const [set, setSet] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(key) ?? "[]"));
    } catch {
      return new Set();
    }
  });
  const toggle = (id: string, open?: boolean) =>
    setSet((prev) => {
      const next = new Set(prev);
      const shouldOpen = open ?? !next.has(id);
      if (shouldOpen) next.add(id);
      else next.delete(id);
      try {
        localStorage.setItem(key, JSON.stringify([...next]));
      } catch {
        /* storage unavailable */
      }
      return next;
    });
  return [set, toggle] as const;
}

export function WorkspaceBrowser({ workspace, onNewNote }: { workspace: Workspace; onNewNote: () => void }) {
  const tree = useTree(workspace.id);
  const userIdForCache = useUi((st) => st.userId);
  useEffect(() => {
    const enc = (tree.data?.notes ?? []).filter((n) => n.encryptionScopeId);
    if (!userIdForCache || !enc.length) return;
    void forgetPlaintextCopies(userIdForCache, enc);
    // Tabs are saved in this browser: never keep a plaintext title of a note that is now encrypted.
    const ui = useUi.getState();
    const tabs = allTabs();
    for (const n of enc) {
      const t = tabs.find((x) => x.key === `note:${n.id}`);
      if (t && t.title !== ENCRYPTED_TITLE_PLACEHOLDER) ui.renameTab(t.key, ENCRYPTED_TITLE_PLACEHOLDER);
    }
  }, [tree.data, userIdForCache]);
  // Keys shared with this person open on their own once their personal key is unlocked.
  const personalUnlocked = usePersonalUnlocked();
  const anyEncrypted = !!workspace.encryptionScopeId || (tree.data?.notes ?? []).some((n) => n.encryptionScopeId);
  const encStatus = useWorkspaceEncryption(workspace.id, anyEncrypted && personalUnlocked);
  useEffect(() => {
    if (!personalUnlocked || !encStatus.data) return;
    for (const sc of encStatus.data.scopes) void unlockWithPersonalKey(sc).catch(() => undefined);
  }, [personalUnlocked, encStatus.data]);
  // The document shown in the active tab is highlighted where it appears in the browser.
  const { activeKey } = useTabs();
  const prefs = usePrefs(workspace.id);
  const qc = useQueryClient();
  const toggleBrowser = useUi((s) => s.toggleBrowser);
  const hidden: WorkspaceSection[] = prefs.data?.hiddenSections ?? [];
  const isProject = workspace.kind === "project";
  const [expanded, toggle] = useExpanded(workspace.id);
  const [recordFilter, setRecordFilter] = useState(() => localStorage.getItem(`brainlog-claude-recfilter:${workspace.id}`) ?? "log");
  const [moveNote, setMoveNote] = useState<NoteRow | null>(null);
  const [newManuscript, setNewManuscript] = useState(false);
  const [prompt, setPrompt] = useState<null | { title: string; label: string; initial?: string; onSubmit: (v: string) => Promise<void> }>(null);
  const manuscripts = useQuery({
    queryKey: qk.manuscripts(workspace.id),
    queryFn: () => get<{ manuscripts: { id: string; title: string; status: string; type: string }[] }>(`/api/workspaces/${workspace.id}/manuscripts`),
    enabled: !hidden.includes("manuscripts"),
  });

  const mailOn = !hidden.includes("correspondence") && !workspace.encryptionScopeId;
  const mail = useQuery({
    queryKey: ckey.list(workspace.id),
    queryFn: () => get<CorrespondenceList>(`/api/workspaces/${workspace.id}/correspondence`),
    enabled: mailOn,
    refetchInterval: 60_000,
  });
  const awaiting = mail.data?.threads.filter((t) => t.status === "open" && t.awaitingReply).length ?? 0;
  const held = mail.data?.address?.held ?? 0;

  const refresh = () => qc.invalidateQueries({ queryKey: qk.tree(workspace.id) });
  const show = (s: WorkspaceSection) => !hidden.includes(s) && (isProject || !["overview", "knowledge", "matrix", "manuscripts", "papers", "code_data"].includes(s));

  const byParent = useMemo(() => {
    const folders = new Map<string | null, FolderRow[]>();
    for (const f of tree.data?.folders ?? []) folders.set(f.parentId, [...(folders.get(f.parentId) ?? []), f]);
    const notes = new Map<string | null, NoteRow[]>();
    for (const n of tree.data?.notes ?? []) notes.set(n.folderId, [...(notes.get(n.folderId) ?? []), n]);
    return { folders, notes };
  }, [tree.data]);

  const records = useMemo(() => {
    const f = RECORD_FILTERS.find((r) => r.key === recordFilter) ?? RECORD_FILTERS[0];
    return (tree.data?.notes ?? [])
      .filter((n) => f.kinds.includes(n.kind))
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      .slice(0, 40);
  }, [tree.data, recordFilter]);

  const newFolder = (parentId: string | null) =>
    setPrompt({
      title: "New folder",
      label: "Folder name",
      onSubmit: async (name) => {
        await post("/api/folders", { workspaceId: workspace.id, parentId, name });
        if (parentId) toggle(parentId, true);
        await refresh();
      },
    });

  const quickNote = async (folderId: string | null) => {
    try {
      const note = await createNoteIn(workspace, { title: "Untitled", folderId });
      if (folderId) toggle(folderId, true);
      await refresh();
      openNote(note.id, note.title, workspace.id);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const folderMenu = (f: FolderRow) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="rounded p-0.5 opacity-60 hover:bg-accent hover:opacity-100 focus-visible:opacity-100" aria-label={`Actions for folder ${f.name}`}>
          <Ellipsis className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => quickNote(f.id)}>New blank note here</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => newFolder(f.id)}>New subfolder</DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() =>
            setPrompt({
              title: "Rename folder",
              label: "Folder name",
              initial: f.name,
              onSubmit: async (name) => {
                await patch(`/api/folders/${f.id}`, { name });
                await refresh();
              },
            })
          }
        >
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={async () => {
            try {
              await patch(`/api/folders/${f.id}`, { parentId: null });
              await refresh();
            } catch (e) {
              toast.error(errorMessage(e));
            }
          }}
          disabled={!f.parentId}
        >
          Move to the top level
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onSelect={async () => {
            try {
              await del(`/api/folders/${f.id}`);
              await refresh();
            } catch (e) {
              toast.error(errorMessage(e));
            }
          }}
        >
          Delete empty folder
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const noteMenu = (n: NoteRow) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="rounded p-0.5 opacity-60 hover:bg-accent hover:opacity-100 focus-visible:opacity-100" aria-label={`Actions for ${n.title}`}>
          <Ellipsis className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => openNote(n.id, n.title, workspace.id)}>Open</DropdownMenuItem>
        <DropdownMenuItem
          disabled={!!n.encryptionScopeId}
          title={n.encryptionScopeId ? "Rename encrypted notes from their title field" : undefined}
          onSelect={() =>
            setPrompt({
              title: "Rename note",
              label: "Title",
              initial: n.title,
              onSubmit: async (title) => {
                await patch(`/api/notes/${n.id}`, { title });
                useUi.getState().renameTab(`note:${n.id}`, title);
                await refresh();
                await qc.invalidateQueries({ queryKey: qk.note(n.id) });
              },
            })
          }
        >
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setMoveNote(n)}>Move to…</DropdownMenuItem>
        {n.kind !== "manuscript_section" && (
          <DropdownMenuItem
            onSelect={async () => {
              if (await setArchived(qc, { type: "note", id: n.id, workspaceId: workspace.id, name: n.title }, true)) useUi.getState().closeTab(`note:${n.id}`);
            }}
          >
            Archive
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={async () => {
            await patch(`/api/notes/${n.id}`, { pinned: !n.pinned });
            await refresh();
          }}
        >
          {n.pinned ? "Unpin from overview" : "Pin to overview"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onSelect={async () => {
            await del(`/api/notes/${n.id}`);
            useUi.getState().closeTab(`note:${n.id}`);
            toast.success(`Moved “${n.title}” to trash`, {
              action: {
                label: "Undo",
                onClick: async () => {
                  await post(`/api/notes/${n.id}/restore`);
                  await refresh();
                },
              },
            });
            await refresh();
          }}
        >
          Move to trash
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const onTreeKey = (e: KeyboardEvent<HTMLElement>) => {
    if (!["ArrowDown", "ArrowUp"].includes(e.key)) return;
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>("[data-tree-item]"));
    const idx = items.indexOf(document.activeElement as HTMLElement);
    if (idx < 0) return;
    e.preventDefault();
    items[Math.max(0, Math.min(items.length - 1, idx + (e.key === "ArrowDown" ? 1 : -1)))]?.focus();
  };

  const renderFolder = (f: FolderRow, depth: number): React.ReactNode => {
    const open = expanded.has(f.id);
    const childFolders = byParent.folders.get(f.id) ?? [];
    const childNotes = byParent.notes.get(f.id) ?? [];
    return (
      <li key={f.id} role="treeitem" aria-expanded={open} aria-selected={false}>
        <div className="group flex items-center gap-1 rounded-md pr-1 hover:bg-accent/70" style={{ paddingLeft: depth * 12 }}>
          <button
            type="button"
            data-tree-item
            className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-1 py-1.5 text-left text-sm"
            onClick={() => toggle(f.id)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") toggle(f.id, true);
              if (e.key === "ArrowLeft") toggle(f.id, false);
            }}
          >
            {open ? <ChevronDown className="size-3.5 shrink-0" aria-hidden /> : <ChevronRight className="size-3.5 shrink-0" aria-hidden />}
            {open ? <FolderOpen className="size-4 shrink-0 text-muted-foreground" aria-hidden /> : <Folder className="size-4 shrink-0 text-muted-foreground" aria-hidden />}
            <span className="truncate">{f.name}</span>
            <span className="sr-only">folder, {childNotes.length} notes</span>
          </button>
          {folderMenu(f)}
        </div>
        {open && (
          <ul role="group">
            {childFolders.map((cf) => renderFolder(cf, depth + 1))}
            {childNotes.map((n) => renderNote(n, depth + 1))}
            {!childFolders.length && !childNotes.length && (
              <li className="py-1 text-xs text-muted-foreground" style={{ paddingLeft: (depth + 1) * 12 + 22 }}>
                Empty folder
              </li>
            )}
          </ul>
        )}
      </li>
    );
  };

  const renderNote = (n: NoteRow, depth: number) => {
    const active = activeKey === `note:${n.id}`;
    return (
      <li key={n.id} role="treeitem" aria-selected={active}>
        <div
          className={cn("group flex items-center gap-1 rounded-md pr-1 hover:bg-accent/70", active && "bg-primary/10 font-medium text-foreground shadow-[inset_2px_0_0_var(--primary)] hover:bg-primary/15")}
          style={{ paddingLeft: depth * 12 + 14 }}
        >
          <button
            type="button"
            data-tree-item
            aria-current={active ? "page" : undefined}
            className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-1 py-1.5 text-left text-sm"
            onClick={() => openNote(n.id, n.title, workspace.id)}
            title={n.encryptionScopeId ? "End-to-end encrypted note" : n.title}
          >
            <KindIcon kind={n.kind} />
            <span className="line-clamp-2 min-w-0 break-words leading-snug">
              <NoteTitle note={n} />
            </span>
            {n.pinned && <Pin className="size-3 shrink-0 text-muted-foreground" aria-label="pinned" />}
          </button>
          {noteMenu(n)}
        </div>
      </li>
    );
  };

  const sectionButton = (icon: React.ReactNode, label: string, onClick: () => void, view?: ViewName) => {
    const active = !!view && activeKey === `view:${view}:${workspace.id}`;
    return (
      <button
        type="button"
        onClick={onClick}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent/70",
          active && "bg-primary/10 font-medium text-foreground shadow-[inset_2px_0_0_var(--primary)] hover:bg-primary/15",
        )}
      >
        {icon}
        <span className="truncate">{label}</span>
      </button>
    );
  };

  return (
    <div role="region" className="flex h-full min-w-0 flex-1 flex-col border-r bg-sidebar lg:w-72 lg:flex-none" aria-label={`${workspace.name} browser`}>
      <div className="flex items-center gap-1 border-b px-3 py-2">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold" title={workspace.name}>
            {workspace.name}
          </h2>
          <p className="text-xs text-muted-foreground">
            {workspace.kind === "notebook" ? "Notebook" : "Project"} · {workspace.memberCount > 1 ? `${workspace.memberCount} members` : "private to you"}
          </p>
        </div>
        <button type="button" onClick={toggleBrowser} className="hidden rounded p-1 hover:bg-accent lg:block" aria-label="Hide the notebook or project panel">
          <PanelLeftClose className="size-4" />
        </button>
      </div>
      {workspace.archivedAt && (
        <div className="space-y-1.5 border-b bg-muted px-3 py-2 text-xs" role="status">
          <p>
            <strong>Archived.</strong> Read-only: nothing here can change until it is unarchived.
          </p>
          {workspace.role === "owner" && (
            <Button size="xs" variant="outline" onClick={() => void setArchived(qc, { type: "workspace", id: workspace.id, name: workspace.name }, false)}>
              Unarchive
            </Button>
          )}
        </div>
      )}
      <div className="thin-scroll relative flex-1 space-y-3 overflow-y-auto p-2">
        {isProject && (
          <div className="space-y-0.5">
            {show("overview") && sectionButton(<LayoutDashboard className="size-4 text-muted-foreground" aria-hidden />, "Overview", () => openView("overview", workspace.id, "Overview"), "overview")}
            {show("knowledge") && sectionButton(<ScrollText className="size-4 text-muted-foreground" aria-hidden />, "Knowledge base", () => openView("knowledge", workspace.id, "Knowledge base"), "knowledge")}
            {show("papers") && sectionButton(<Library className="size-4 text-muted-foreground" aria-hidden />, "Papers & library", () => openView("library", workspace.id, "Library"), "library")}
            {show("code_data") && sectionButton(<FileCode2 className="size-4 text-muted-foreground" aria-hidden />, "Code and data", () => openView("code_data", workspace.id, "Code and data"), "code_data")}
            {show("matrix") && sectionButton(<Table2 className="size-4 text-muted-foreground" aria-hidden />, "Literature matrix", () => openView("matrix", workspace.id, "Literature matrix"), "matrix")}
            {show("backlog") && sectionButton(<Kanban className="size-4 text-muted-foreground" aria-hidden />, "Backlog", () => openView("backlog", workspace.id, "Backlog"), "backlog")}
            {show("files") && sectionButton(<Paperclip className="size-4 text-muted-foreground" aria-hidden />, "Files", () => openView("files", workspace.id, "Files"), "files")}
          </div>
        )}

        {show("notes") && (
          <section aria-labelledby={`notes-h-${workspace.id}`}>
            <div className="flex items-center gap-1 px-2 pb-1">
              <h3 id={`notes-h-${workspace.id}`} className="flex-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Notes
              </h3>
              {/* Archived: nothing new until it is unarchived. */}
              {!workspace.archivedAt && (
                <>
                  <Button size="icon-xs" variant="ghost" onClick={() => quickNote(null)} aria-label="New blank note at the top level" title="New blank note">
                    <FilePlus />
                  </Button>
                  <Button size="icon-xs" variant="ghost" onClick={onNewNote} aria-label="New note from template" title="New note from template">
                    <Plus />
                  </Button>
                  <Button size="icon-xs" variant="ghost" onClick={() => newFolder(null)} aria-label="New folder" title="New folder">
                    <FolderPlus />
                  </Button>
                </>
              )}
            </div>
            {tree.isLoading && <p className="px-2 text-sm text-muted-foreground">Loading…</p>}
            {tree.isError && <p className="px-2 text-sm text-destructive">Could not load notes: {errorMessage(tree.error)}</p>}
            <ul role="tree" aria-label="Folders and notes" onKeyDown={onTreeKey}>
              {(byParent.folders.get(null) ?? []).map((f) => renderFolder(f, 0))}
              {(byParent.notes.get(null) ?? []).map((n) => renderNote(n, 0))}
            </ul>
            {tree.data && !tree.data.notes.length && !tree.data.folders.length && (
              <p className="px-2 py-2 text-sm text-muted-foreground">No notes yet. Create a blank note or start from a template.</p>
            )}
          </section>
        )}

        {show("records") && (
          <section aria-labelledby={`rec-h-${workspace.id}`}>
            <div className="flex items-center gap-1 px-2 pb-1">
              <h3 id={`rec-h-${workspace.id}`} className="flex-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Research records
              </h3>
            </div>
            <label className="sr-only" htmlFor={`recfilter-${workspace.id}`}>
              Filter records
            </label>
            <select
              id={`recfilter-${workspace.id}`}
              className="mx-2 mb-1 w-[calc(100%-1rem)] rounded-md border bg-background px-2 py-1 text-sm"
              value={recordFilter}
              onChange={(e) => {
                setRecordFilter(e.target.value);
                try {
                  localStorage.setItem(`brainlog-claude-recfilter:${workspace.id}`, e.target.value);
                } catch {
                  /* ignore */
                }
              }}
            >
              {RECORD_FILTERS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
            <ul aria-label="Filtered research records">
              {records.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    aria-current={activeKey === `note:${n.id}` ? "page" : undefined}
                    className={cn(
                      "flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent/70",
                      activeKey === `note:${n.id}` && "bg-primary/10 font-medium text-foreground",
                    )}
                    onClick={() => openNote(n.id, n.title, workspace.id)}
                    title={n.encryptionScopeId ? "End-to-end encrypted note" : n.title}
                  >
                    <KindIcon kind={n.kind} />
                    <span className="truncate">
                      <NoteTitle note={n} />
                    </span>
                  </button>
                </li>
              ))}
              {tree.data && !records.length && <li className="px-2 py-1 text-sm text-muted-foreground">None recorded.</li>}
            </ul>
          </section>
        )}

        {show("manuscripts") && (
          <section aria-labelledby={`ms-h-${workspace.id}`}>
            <div className="flex items-center gap-1 px-2 pb-1">
              <h3 id={`ms-h-${workspace.id}`} className="flex-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Reports & manuscripts
              </h3>
              <Button
                size="icon-xs"
                variant="ghost"
                aria-label="New report or manuscript"
                title="New report or manuscript"
                onClick={() => setNewManuscript(true)}
              >
                <Plus />
              </Button>
            </div>
            <ul>
              {manuscripts.data?.manuscripts.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    aria-current={activeKey === `view:manuscript:${m.id}` ? "page" : undefined}
                    className={cn(
                      "flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent/70",
                      activeKey === `view:manuscript:${m.id}` && "bg-primary/10 font-medium text-foreground",
                    )}
                    onClick={() => openView("manuscript", workspace.id, m.title, undefined, m.id)}
                  >
                    <FileType className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="truncate">{m.title}</span>
                  </button>
                </li>
              ))}
              {manuscripts.data && !manuscripts.data.manuscripts.length && <li className="px-2 py-1 text-sm text-muted-foreground">No reports or manuscripts yet.</li>}
            </ul>
          </section>
        )}

        {show("correspondence") && mailOn && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-current={activeKey === `view:correspondence:${workspace.id}` ? "page" : undefined}
              className={cn(
                "flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent/70",
                activeKey === `view:correspondence:${workspace.id}` && "bg-primary/10 font-medium text-foreground shadow-[inset_2px_0_0_var(--primary)] hover:bg-primary/15",
              )}
              onClick={() => openView("correspondence", workspace.id, "Correspondence")}
            >
              <Mail className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate">Correspondence</span>
              {awaiting > 0 && <span className="ml-auto rounded-full bg-warning/15 px-1.5 text-[0.65rem] text-warning">{awaiting} to answer</span>}
              {held > 0 && <span className={cn("rounded-full bg-warning px-1.5 text-[0.65rem] text-white", !awaiting && "ml-auto")}>{held} held</span>}
            </button>
            <Button size="icon-xs" variant="ghost" aria-label="Add emails" title="Add emails" onClick={() => openView("correspondence", workspace.id, "Correspondence", { add: String(Date.now()) })}>
              <MailPlus />
            </Button>
          </div>
        )}
      </div>
      <div className="space-y-0.5 border-t p-2">
        {sectionButton(<Trash className="size-4 text-muted-foreground" aria-hidden />, "Trash", () => openView("trash", workspace.id, "Trash"), "trash")}
        {sectionButton(
          <SlidersHorizontal className="size-4 text-muted-foreground" aria-hidden />,
          workspace.kind === "notebook" ? "Notebook settings" : "Project settings",
          () => openView("settings", workspace.id, "Settings"),
          "settings",
        )}
      </div>
      {newManuscript && <NewManuscriptDialog workspace={workspace} onClose={() => setNewManuscript(false)} />}
      {moveNote && (
        <MoveDialog
          open
          onOpenChange={(o) => !o && setMoveNote(null)}
          noteId={moveNote.id}
          noteTitle={moveNote.title}
          currentWorkspaceId={workspace.id}
        />
      )}
      {prompt && (
        <PromptDialog
          open
          title={prompt.title}
          label={prompt.label}
          initial={prompt.initial}
          onOpenChange={(o) => !o && setPrompt(null)}
          onSubmit={async (v) => {
            try {
              await prompt.onSubmit(v);
              setPrompt(null);
            } catch (e) {
              toast.error(errorMessage(e));
            }
          }}
        />
      )}
    </div>
  );
}

export const SECTION_LABELS = WORKSPACE_SECTIONS.labels;
